/**
 * SavePlaybookPanel — the thin, real UI over the churn-save playbook engine
 * (savePlaybook.ts).
 *
 * Everything shown here is a pure projection of live props: trigger
 * enrollments, the blast-radius-simulated write-set (with per-account
 * carve-out toggles), the triaged approve lanes, per-step earned-autonomy
 * status, and closed-loop effectiveness stats. The ONLY writes happen when
 * the operator clicks "Run this playbook" (or the one-gesture low-risk
 * approve): each included step is queued through the SAME governed
 * approvals pipeline every agent uses (queueApproval → Dashboard inbox →
 * resolveApproval), and steps whose class has EARNED auto-send (N clean
 * prior approvals, zero reversals) are resolved immediately with a post-hoc
 * review note. The batch note is narrated by the real Argo worker when it
 * is configured; otherwise the deterministic simulation summary is used —
 * the run never blocks on a worker.
 */

import { useEffect, useMemo, useState } from 'react';
import { LifeBuoy, Play, CheckCheck, ShieldCheck, Scissors, Bot, AlertTriangle } from 'lucide-react';
import { GlassPanel } from '../../components/GlassPanel';
import { C } from '../tokens';
import { isConfigured as agentWorkerConfigured, runAgent } from '../agent/argoWorker';
import { queueApproval, resolveApproval, listApprovals, subscribeApprovals } from '../agent/approvals';
import type { ApprovalItem } from '../db/types';
import type { CreateTaskAction } from '../agent/types';
import type { ChurnRiskResult } from './churnRisk';
import {
  evaluateTriggers,
  buildPlaybookRun,
  simulateBatch,
  triageBatch,
  resolveAutonomy,
  rollupPlaybookEffectiveness,
  ledgerEntriesFromApprovals,
  DEFAULT_SAVE_TRIGGER,
  AUTONOMY_CLEAN_APPROVALS,
  type PlayOutcome,
  type SaveAccount,
  type SavePlaybookStep,
  type TriagedStep,
} from './savePlaybook';

export interface SavePlaybookPanelProps {
  /** Accounts eligible for enrollment (structural — derived at the view). */
  accounts: SaveAccount[];
  /** REAL churn-risk fusion results — the only enrollment signal. */
  riskResults: ChurnRiskResult[];
  /** Verified run outcomes, when any have closed (effectiveness stats). */
  outcomes?: PlayOutcome[];
  /** Whether the operator may queue writes (role gate from the view). */
  canRun?: boolean;
  /** True while the underlying snapshots are still loading. */
  loading?: boolean;
}

function usd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

const KIND_LABEL: Record<SavePlaybookStep['kind'], string> = {
  'save-email': 'Save email',
  task: 'Check-in task',
  'exec-escalation': 'Exec escalation',
};

export function SavePlaybookPanel({
  accounts,
  riskResults,
  outcomes = [],
  canRun = false,
  loading = false,
}: SavePlaybookPanelProps) {
  const [carveOuts, setCarveOuts] = useState<Set<string>>(new Set());
  const [approvals, setApprovals] = useState<ApprovalItem[]>([]);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const unsub = subscribeApprovals((rows) => active && setApprovals(rows));
    return () => {
      active = false;
      unsub();
    };
  }, []);

  // Pure projections — recomputed from live props so they can never drift.
  const enrollments = useMemo(
    () => evaluateTriggers(accounts, riskResults),
    [accounts, riskResults],
  );
  const runs = useMemo(() => enrollments.map(buildPlaybookRun), [enrollments]);
  const sim = useMemo(
    () => simulateBatch(runs, { carveOuts: [...carveOuts] }),
    [runs, carveOuts],
  );
  const includedRuns = useMemo(
    () => runs.filter((r) => !carveOuts.has(r.enrollment.accountId)),
    [runs, carveOuts],
  );
  const triage = useMemo(() => triageBatch(includedRuns), [includedRuns]);
  // Earned autonomy, derived from the REAL resolved-approvals history.
  const ledgerHistory = useMemo(() => ledgerEntriesFromApprovals(approvals), [approvals]);
  const effectiveness = useMemo(() => rollupPlaybookEffectiveness(outcomes), [outcomes]);

  function toggleCarveOut(accountId: string) {
    setCarveOuts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  }

  /**
   * Queue the given steps through the governed approvals pipeline. De-dupes
   * by task title against pending proposals (same rule as the health agent);
   * steps whose class has EARNED auto-send are resolved immediately — the
   * approval record stays in the ledger for post-hoc review.
   */
  async function queueSteps(steps: SavePlaybookStep[], label: string) {
    setRunning(true);
    setRunResult(null);
    setRunError(null);
    try {
      // Agentic narration via the real worker; deterministic fallback always.
      let narrative = sim.summary;
      if (agentWorkerConfigured()) {
        try {
          const { text } = await runAgent(
            {
              id: 'save-playbook',
              name: 'Churn-Save Playbook',
              template: 'churn-save',
              persona: 'Retention operator who explains each intervention plainly',
            },
            { simulation: sim, steps: steps.map((s) => ({ kind: s.kind, title: s.title })) },
          );
          if (text.trim()) narrative = text.trim();
        } catch {
          // Worker unreachable — the deterministic summary stands.
        }
      }

      const pending = await listApprovals('pending');
      const queuedTitles = new Set(
        pending
          .filter((a) => a.action.kind === 'create_task')
          .map((a) => (a.action as CreateTaskAction).title),
      );

      let proposed = 0;
      let autoSent = 0;
      let skipped = 0;
      for (const step of steps) {
        if (queuedTitles.has(step.action.title)) {
          skipped++;
          continue;
        }
        const grant = resolveAutonomy(step, ledgerHistory);
        const run = runs.find((r) => r.id === step.runId);
        const note = [run?.enrollment.reason, grant.reason, narrative]
          .filter(Boolean)
          .join(' · ');
        const record = await queueApproval(step.action, 'scheduler', note);
        queuedTitles.add(step.action.title);
        if (grant.mode === 'auto-send') {
          // Earned autonomy: execute now; the sealed record IS the post-hoc
          // review trail. A reversal there resets the ladder to human.
          await resolveApproval(record.id, 'approved');
          autoSent++;
        } else {
          proposed++;
        }
      }

      const bits: string[] = [];
      if (proposed > 0) bits.push(`${proposed} queued for approval`);
      if (autoSent > 0) bits.push(`${autoSent} auto-sent (earned autonomy, post-hoc review)`);
      if (skipped > 0) bits.push(`${skipped} already awaiting approval`);
      setRunResult(
        bits.length > 0 ? `${label}: ${bits.join(' · ')}` : `${label}: nothing to queue`,
      );
    } catch {
      setRunError('Run failed — nothing further was queued. Try again.');
    } finally {
      setRunning(false);
    }
  }

  const includedSteps = includedRuns.flatMap((r) => r.steps);
  const lowRiskSteps = triage.lowRisk.map((t) => t.step);

  return (
    <GlassPanel variant="default" style={{ padding: '18px 20px' }}>
      <div
        className="dash-section-head"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            aria-hidden="true"
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              display: 'inline-grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.18), rgba(0,130,243,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: C.magenta,
            }}
          >
            <LifeBuoy size={16} strokeWidth={2} />
          </span>
          <div>
            <span className="t-eyebrow">Churn-save playbook</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>
              Governed save runs
            </h2>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {enrollments.length > 0 && (
            <span className="glass-pill glass-pill--magenta">{enrollments.length} enrolled</span>
          )}
          {sim.totalArrAtRisk > 0 && (
            <span className="glass-pill glass-pill--orange">{usd(sim.totalArrAtRisk)} at risk</span>
          )}
          {canRun && (
            <>
              {lowRiskSteps.length > 0 && (
                <button
                  className="glass-btn"
                  disabled={running || loading}
                  onClick={() => void queueSteps(lowRiskSteps, 'Low-risk batch')}
                  title="One gesture: queue every reversible, low-blast-radius step (save-email drafts + check-in tasks). Exec escalations stay in per-item review."
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <CheckCheck size={13} /> Approve {lowRiskSteps.length} low-risk
                </button>
              )}
              <button
                className="glass-btn glass-btn--primary"
                disabled={running || loading || includedSteps.length === 0}
                onClick={() => void queueSteps(includedSteps, 'Playbook run')}
                title="Queues every included step (per the simulation below) into the Dashboard approvals inbox. Steps with earned autonomy auto-send with post-hoc review."
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
              >
                <Play size={13} /> {running ? 'Running…' : 'Run this playbook'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="t-meta" style={{ margin: '0 0 10px' }}>
        Trigger: <code>{DEFAULT_SAVE_TRIGGER.condition}</code> over the live churn-risk fusion —
        every write below is simulated first, reversible, and human-governed.
        {agentWorkerConfigured() ? ' Narration: Argo worker.' : ' Narration: deterministic.'}
      </div>

      {runResult && (
        <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px' }}>
          <Bot size={12} color={C.magentaSoft} /> {runResult}
        </div>
      )}
      {runError && (
        <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 10px', color: C.magenta }}>
          <AlertTriangle size={12} /> {runError}
        </div>
      )}

      {loading ? (
        <div className="crm-empty" style={{ padding: '20px 0' }}>
          <LifeBuoy size={20} strokeWidth={1.75} />
          <span>Loading live account signals…</span>
        </div>
      ) : enrollments.length === 0 ? (
        <div className="crm-empty" style={{ padding: '20px 0' }}>
          <ShieldCheck size={20} strokeWidth={1.75} />
          <span>No account currently meets the trigger — nothing to save. The playbook enrolls automatically when one does.</span>
        </div>
      ) : (
        <>
          {/* Batch simulation: the full write-set with carve-out toggles. */}
          <div className="t-meta" style={{ margin: '0 0 6px', fontWeight: 600 }}>
            Batch simulation — {sim.summary}
          </div>
          <ul style={{ listStyle: 'none', margin: '0 0 12px', padding: 0, display: 'grid', gap: 6 }}>
            {runs.map((run) => {
              const carved = carveOuts.has(run.enrollment.accountId);
              return (
                <li
                  key={run.id}
                  className="glass-row"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', opacity: carved ? 0.55 : 1 }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span className="t-card-title" style={{ display: 'block' }}>
                      {run.enrollment.accountName}
                      {run.enrollment.arrAtRisk > 0 && (
                        <span className="t-meta"> · {usd(run.enrollment.arrAtRisk)} ARR</span>
                      )}
                    </span>
                    <span className="t-meta" style={{ display: 'block', marginTop: 2 }}>
                      {run.enrollment.reason}
                    </span>
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                      {sim.writes
                        .filter((w) => w.runId === run.id)
                        .map((w) => (
                          <span
                            key={w.stepId}
                            className="glass-pill"
                            style={{ fontSize: 10, textDecoration: w.excluded ? 'line-through' : 'none' }}
                            title={w.diff}
                          >
                            {KIND_LABEL[w.kind]}
                          </span>
                        ))}
                    </span>
                  </span>
                  <button
                    className="glass-btn"
                    onClick={() => toggleCarveOut(run.enrollment.accountId)}
                    title={carved ? 'Include this account in the batch again' : 'Carve this account out of the batch — its writes are excluded from the run'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flex: '0 0 auto' }}
                  >
                    <Scissors size={12} /> {carved ? 'Carved out' : 'Carve out'}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Triage lanes: high blast first; earned autonomy per step. */}
          <div className="t-meta" style={{ margin: '0 0 6px', fontWeight: 600 }}>
            Triage — blast radius first · {triage.lowRisk.length} one-gesture low-risk ·{' '}
            {triage.needsReview.length} need review
          </div>
          <ul style={{ listStyle: 'none', margin: '0 0 12px', padding: 0, display: 'grid', gap: 4 }}>
            {triage.ranked.map((t: TriagedStep) => {
              const grant = resolveAutonomy(t.step, ledgerHistory);
              return (
                <li
                  key={t.step.id}
                  className="glass-row"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}
                >
                  <span
                    className={t.lowRisk ? 'glass-pill glass-pill--blue' : 'glass-pill glass-pill--orange'}
                    style={{ flex: '0 0 auto', fontSize: 10 }}
                  >
                    {t.lowRisk ? 'low risk' : 'review'}
                  </span>
                  <span className="t-meta" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {t.step.title} — {t.rationale}
                  </span>
                  <span
                    className="glass-pill"
                    style={{ flex: '0 0 auto', fontSize: 10 }}
                    title={grant.reason}
                  >
                    {grant.mode === 'auto-send'
                      ? 'auto-send · post-hoc review'
                      : `human · ${grant.cleanApprovals}/${AUTONOMY_CLEAN_APPROVALS} earned`}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {/* Closed-loop effectiveness: only verified outcomes ever count. */}
      <div className="t-meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <ShieldCheck size={12} aria-hidden="true" />
        {effectiveness.atRisk > 0 ? (
          <span>
            Effectiveness: <strong>{effectiveness.savedOfAtRisk}</strong> (
            {Math.round(effectiveness.conversionRate * 100)}% conversion)
            {effectiveness.arrRetained > 0 ? ` · ${usd(effectiveness.arrRetained)} ARR retained` : ''}
          </span>
        ) : (
          <span>Effectiveness: no runs have closed their verification window yet — stats appear only from verified outcomes, never projections.</span>
        )}
      </div>
    </GlassPanel>
  );
}
